import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs/internal/Subscription';
import * as _ from 'underscore';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrFilter } from '../../../models/dr-filter';
import { Endpoint, EntityType, FilterAttribute, FilterOperator, HelpIndex, MessageType } from '../../../models/enums';
import { Workflow } from '../../../models/workflow';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { TreeDataLoaderService } from '../../../services/tree/treeDataLoader.service';
import { FilterComponent } from '../../filter/filter.component';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';

@Component({
    standalone: false,
    selector: 'res-find-in-tree-modal',
    templateUrl: './find-in-tree-modal.component.html',
    styleUrls: ['./find-in-tree-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class FindInTreeModalComponent extends BaseModalComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(GenericTreeComponent, { static: true }) treeComponent: GenericTreeComponent;
    @ViewChild(FilterComponent) filterComponent: FilterComponent;

    modalAutomationId = 'target-picker-tree';
    helpIdx = HelpIndex.find_in_tree_dlg;
    modalClass = 'find-in-tree-modal';
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    isBusy = false;
    isBusyMessage = 'Loading...';
    filterValue: string;
    selectedNode: GenericTreeNode;
    hideEmptyViews: boolean;
    inputAutoFocused = true;
    tmpNodesWithoutFiltering: Array<GenericTreeNode>;
    childrenCleared: boolean;
    firstFilterAttempt: boolean;
    debounceFilter = _.debounce(this.filterTreeAfterBounce, 1000);
    private entityChangedEvent: Subscription;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private treeDataService: TreeDataLoaderService) {
        super(modalService, modalRef);

        this.treeDataService.automationId = this.modalAutomationId;
        this.commonService.getMessage(MessageType.refreshTreeNodesWithLoadingInProgress).subscribe(() => {
            this.treeComponent.treeDataUpdated();
        })

        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            const entityType = this.commonService.updateStatusTypeToEntityType(data);
            if (entityType) {
                this.treeDataService.loadNodeData(this.getNodeByEntityType(entityType), false).then(() => {
                    this.treeComponent.treeDataUpdated();
                });
            }
        });
    }

    ngOnInit(): void {
        this.treeDataService.createTreeNodes();
    }

    ngAfterViewInit(): void {
        this.treeComponent.setData(this.treeDataService.nodes);
        this.treeDataService.loadData().then(() => {
            this.treeComponent.treeDataUpdated();
        });
    }

    loadDataForFilter(filterValue = null): void {
        if (filterValue) {
            if (!this.firstFilterAttempt) {
                this.firstFilterAttempt = true;
                // store initial data in order to be restored again after filter is cleared
                this.tmpNodesWithoutFiltering = cloneDeep(this.treeDataService.nodes);
            }
            const requestData = { handle: this.commonService.currentProject.handle, maxCountOfObjects: 0, filter: new DrFilter({ attribute: FilterAttribute.NAME, operator: FilterOperator.LIKE, value: filterValue }) };

            this.isBusy = true;
            this.restService.post(Endpoint.orgManager_getCase, requestData).then(responseGetCase => {
                this.setDataFromResponse(responseGetCase, requestData.filter);
                this.isBusy = false;
            });
        }
    }

    expandNodes(nodeTypesToExpand: Array<EntityType>): void {
        if (nodeTypesToExpand) {
            nodeTypesToExpand.forEach(nodeType => {
                const nodeName = nodeType === EntityType.searchHistory ? 'searches' : nodeType.toLowerCase();
                const nodeToExpand = _.find(this.treeDataService.nodes, node => node.entityType === nodeType || node.name.toLowerCase() === nodeName);
                if (nodeToExpand) {
                    this.treeComponent.treeControl.expand(nodeToExpand);
                }
            })
        }
    }

    setDataFromResponse(treeDataToSet: any, filterValue = null): void {
        if (treeDataToSet) {
            this.childrenCleared = false;
            this.clearAllChildrenFromAllNodes();

            Array.from(this.treeDataService.nodeMap.keys()).forEach(entityType => {
                this.populateDataForNodeType(treeDataToSet, entityType, filterValue);
            });

            if (this.childrenCleared) {
                this.treeComponent.setData(this.treeDataService.nodes);
                this.treeComponent.treeDataUpdated();
            }
        }

        if (filterValue) {
            this.treeComponent.treeControl.expandAll();
        }
    }

    populateDataForNodeType(serverResponse, nodeEntityType: EntityType, filterValue = null) {
        let listOfItems = null;
        if (serverResponse) {
            switch (nodeEntityType) {
                case EntityType.import: {
                    listOfItems = serverResponse.listCorpusSetCorporaResponseMessage?.corpora;
                    break;
                }
                case EntityType.export: {
                    listOfItems = serverResponse.listExportStreamsResponseMessage?.exportStreams;
                    break;
                }
                case EntityType.custodian: {
                    listOfItems = serverResponse.listCustodianViewsResponseMessage?.metadataViews;
                    break;
                }
                case EntityType.mediaID: {
                    listOfItems = serverResponse.listMediaidViewsResponseMessage?.metadataViews;
                    break;
                }
                case EntityType.batch: {
                    listOfItems = serverResponse.listBatchViewsResponseMessage?.metadataViews;
                    break;
                }
                case EntityType.folder: {
                    listOfItems = serverResponse.listDynamicFoldersResponseMessage?.dynamicFolders;
                    break;
                }
                case EntityType.tagView: {
                    listOfItems = serverResponse.listTagsResponseMessage?.views;
                    break;
                }
                case EntityType.searchHistory: {
                    listOfItems = serverResponse.listSearchHistoryResponseMessage?.views;
                    break;
                }
                case EntityType.savedSearch: {
                    listOfItems = serverResponse.listRulesBasedSavedSearchesResponseMessage?.activeRules;
                    break;
                }
                case EntityType.workFlow: {
                    listOfItems = serverResponse.listWorkFlowsResponseMessage?.workFlows;
                    break;
                }
                case EntityType.comparison: {
                    listOfItems = serverResponse.listComparisonsResponseMessage?.comparisons;
                    break;
                }
                case EntityType.profile: {
                    listOfItems = serverResponse.listProfilesResponseMessage?.profiles;
                    break;
                }
            }
            if (listOfItems?.length > 0) {
                if (nodeEntityType === EntityType.searchHistory) {
                    this.treeDataService.setSearchResultsData(listOfItems, serverResponse.listSearchHistoryResponseMessage.totalCount, filterValue);
                } else {
                    this.treeDataService.nodeMap.get(nodeEntityType).setChildren(listOfItems);
                    this.getNodeByEntityType(nodeEntityType).children = this.treeDataService.nodeMap.get(nodeEntityType).children;
                    if (nodeEntityType === EntityType.workFlow) {
                        this.treeDataService.setWorkflowChildren();
                        this.childrenCleared = true;
                    }
                }
            }
        }
    }

    clearAllChildrenFromAllNodes() {
        const nodesTypesToBeCleared = [EntityType.import, EntityType.export, EntityType.custodian, EntityType.mediaID, EntityType.batch, EntityType.tagView, EntityType.folder, EntityType.comparison,
        EntityType.profile, EntityType.searchResult, EntityType.savedSearch, EntityType.workFlow];

        nodesTypesToBeCleared.forEach(nt => this.resetChildrenForEntityTypeNode(nt));

        if (!this.childrenCleared) {
            this.childrenCleared = true;
        }
    }

    resetChildrenForEntityTypeNode(nodeType: EntityType) {
        this.treeDataService.nodeMap.get(nodeType).children = [];
        const nodeToClearChildren = this.getNodeByEntityType(nodeType);
        if (nodeToClearChildren?.children) {
            nodeToClearChildren.children = [];
        }
    }

    getNodeByEntityType(entityTypeOfNode: EntityType): GenericTreeNode {
        let nodeToReturn = this.treeDataService.nodes.find(n => n.entityType === entityTypeOfNode);
        if (nodeToReturn) {
            return nodeToReturn;
        } else {
            for (let index = 0; index < this.treeDataService.nodes.length; index++) {
                if (this.treeDataService.nodes[index].children?.length > 0) {
                    nodeToReturn = this.treeDataService.nodes[index].children.find(n => n.entityType === entityTypeOfNode);
                    if (nodeToReturn) {
                        return nodeToReturn;
                    }
                }
            }
        }
        return null;
    }

    isPrimaryButtonEnabled(): boolean {
        return Boolean(this.treeComponent?.selectedNode);
    }

    doubleClickAction(node: GenericTreeNode) {
        this.ok();
    }

    ok(): void {
        if (this.treeComponent.selectedNode) {
            this.closeModal(true, { target: this.treeComponent.selectedNode });
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode): void => {
        if (node?.isButton && node.parent?.requestInfo?.data) {
            const returnedPromise = this.treeDataService.loadMoreResults(node.parent.requestInfo.data.count + 10, node.parent)
            if (returnedPromise) {
                returnedPromise.then(() => {
                    this.treeComponent.treeDataUpdated();
                    this.expandNodes([EntityType.searchHistory]);
                })
            }
        } else {
            this.selectedNode = node;
        }
    }

    filterData(value: string): void {
        this.debounceFilter(value);
    }

    filterTreeAfterBounce(filterVal: string): void {
        this.performFilterData(filterVal);
    }

    performFilterData(value: string): void {
        this.filterValue = value;

        if (this.filterValue.length > 1) {
            this.loadDataForFilter(this.filterValue);
        }

        if (this.filterValue === '') {
            this.restoreTreeDataAfterFilterClear();
        }
    }

    restoreTreeDataAfterFilterClear(): void {
        if (this.tmpNodesWithoutFiltering) {
            this.treeDataService.nodes = cloneDeep(this.tmpNodesWithoutFiltering);
            this.tmpNodesWithoutFiltering = null;
            this.firstFilterAttempt = false;
        }
        this.treeComponent.setData(this.treeDataService.nodes);
        this.treeComponent.treeControl.collapseAll();
        this.treeComponent.selectedNode = null;
        this.treeComponent.treeDataUpdated();
        this.expandNodes([EntityType.projectData, EntityType.searchHistory, EntityType.analytics]);
    }

    hideEmptyViewsChanged(): void {
        this.setFilterByOnEachNodeFromList(this.treeDataService.nodes);
        if (this.tmpNodesWithoutFiltering) {
            this.setFilterByOnEachNodeFromList(this.tmpNodesWithoutFiltering);
        }
        this.treeComponent.selectedNode = null;
        this.treeComponent.treeDataUpdated();
        this.selectedNode = null;
    }

    setFilterByOnEachNodeFromList(listOfNodes: Array<GenericTreeNode>): void {
        if (listOfNodes?.length > 0) {
            const filterByForNode = (node: GenericTreeNode) => node.isButton ||
                ((this.hideEmptyViews ? (node.customNumber > 0 || (node.data instanceof Workflow && (node.data as Workflow).resultCount > 0)) : true));


            listOfNodes.forEach(node => {
                if (node.entityType === EntityType.import || node.entityType === EntityType.export) {
                    node.filterBy = filterByForNode;
                }
                if (node.children?.length > 0) {
                    node.children.forEach(child => child.filterBy = filterByForNode);
                }
            });
        }
    }

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
    }
}
